/* The JavaScript side of the GrowDaily phone theme and its quick launches.
 *
 * Same shape as `playBilling.js`: talk to the native plugin through the global
 * Capacitor bridge, and behave sensibly when there is no bridge at all -- the
 * same code runs in a desktop browser, where none of this exists.
 *
 * The one rule with teeth is in `applyTheme`: only the surfaces the user
 * actually chose are ever rendered or sent. Replacing somebody's home screen
 * because they asked for a lock screen is not a bug they would forgive, and
 * it is not something they can undo -- the previous wallpaper is gone.
 */
import { surfacesFor, renderWallpaper } from './phoneTheme';

const OFFLINE = {
    tilePrompt: false,
    shortcutPin: false,
    assistantDialog: false,
    wallpaper: false,
    lockWallpaper: false,
    isAssistant: false,
    native: false,
};

export function phoneBridge() {
    if (typeof window === 'undefined') return null;
    const native = window.Capacitor?.isNativePlatform?.();
    if (!native) return null;
    return window.Capacitor?.Plugins?.GrowDailyPhone || null;
}

/**
 * What this device will actually let us do.
 *
 * Never throws and never guesses: a browser, an old phone and a locked-down
 * work profile all come back with everything false, so the screen can show
 * what is possible rather than buttons that fail when tapped.
 */
export async function capabilities() {
    const bridge = phoneBridge();
    if (!bridge) return { ...OFFLINE };
    try {
        const reported = await bridge.capabilities();
        return {
            ...OFFLINE, ...reported, native: true,
            // New controls remain hidden for old app versions and unsupported launchers.
            shortcutPin: reported?.shortcutPin === true && typeof bridge.addSnapShortcut === 'function',
        };
    } catch {
        return { ...OFFLINE, native: true };
    }
}

/** Request the launcher confirmation, only in response to an explicit tap.
 * A request is not evidence that the user accepted or that a shortcut was pinned.
 */
export async function addSnapShortcut() {
    const bridge = phoneBridge();
    if (typeof bridge?.addSnapShortcut !== 'function') return { ok: false, reason: 'unsupported' };
    try {
        const result = await bridge.addSnapShortcut();
        return result?.requested === true
            ? { ok: true, requested: true }
            : { ok: false, reason: 'request_failed' };
    } catch (e) {
        return { ok: false, reason: String(e?.message || e || 'request_failed') };
    }
}

export async function addQuickTile() {
    const bridge = phoneBridge();
    if (!bridge) return { ok: false, reason: 'unsupported' };
    try {
        await bridge.addQuickTile();
        return { ok: true };
    } catch (e) {
        return { ok: false, reason: String(e?.message || e || 'failed') };
    }
}

export async function requestAssistantRole() {
    const bridge = phoneBridge();
    if (!bridge) return { ok: false, reason: 'unsupported' };
    try {
        const result = await bridge.requestAssistantRole();
        return { ok: true, ...result };
    } catch (e) {
        return { ok: false, reason: String(e?.message || e || 'failed') };
    }
}

/**
 * Draw and apply the theme.
 *
 * `render` is injectable so this can be tested without a canvas; the default
 * is the real generator. `screen` likewise, so a test does not depend on
 * whatever jsdom thinks the window size is.
 */
export async function applyTheme({
    choice,
    variant = 'forest',
    screen = null,
    render = renderWallpaper,
} = {}) {
    const surfaces = surfacesFor(choice);
    if (!surfaces.length) return { ok: false, reason: 'nothing_chosen' };

    const bridge = phoneBridge();
    if (!bridge) return { ok: false, reason: 'unsupported' };

    const size = screen || {
        width: window.screen?.width || window.innerWidth,
        height: window.screen?.height || window.innerHeight,
        ratio: window.devicePixelRatio || 1,
    };

    // Only what was chosen is drawn. Rendering the other surface "just in
    // case" would be a 2560px canvas thrown away at best, and at worst one
    // stray property away from overwriting a wallpaper nobody asked about.
    const payload = {};
    for (const surface of surfaces) {
        payload[surface] = render({ ...size, surface, variant });
    }

    try {
        const applied = await bridge.applyWallpaper(payload);
        return { ok: true, surfaces, applied };
    } catch (e) {
        return { ok: false, reason: String(e?.message || e || 'failed') };
    }
}

/* Volume keys as a camera shutter.
 *
 * The Android app forwards the volume rocker to the page as a window event
 * ONLY while this is switched on (the Lyn-ID viewfinder is up), so the keys
 * keep changing the volume everywhere else. Harmless no-op in a browser.
 */
export const VOLUME_SHUTTER_EVENT = 'growdailyVolumeShutter';

export function setVolumeShutter(enabled) {
    const bridge = phoneBridge();
    if (!bridge?.setVolumeShutter) return;
    try { Promise.resolve(bridge.setVolumeShutter({ enabled: !!enabled })).catch(() => {}); } catch { /* ignore */ }
}
